import { compact, get, trim, uniq, uniqBy } from 'lodash';

// Wallets format phone numbers for display ("+61 416 527 518", "(04) 1652-7518"); the guest
// checkout schema only accepts the bare digits with an optional leading "+".
const normaliseMobile = phoneNumber => (phoneNumber ? phoneNumber.replace(/[^\d+]/g, '') : '');

// Google Pay only returns the billing address's single `name` line, so split it on the first space.
const splitFullName = fullName => {
  const [givenName = '', ...rest] = trim(fullName).split(/\s+/);
  return { givenName, surname: rest.join(' ') };
};

/**
 * Maps the contact details an Apple Pay sheet returns (`ApplePayPaymentContact`) onto the guest
 * checkout customer details shape. Missing fields come back as empty strings so the result can
 * still be stored on CartStore (whose model requires strings) and prefill the guest form.
 */
const guestDetailsFromApplePayContact = shippingContact => ({
  givenName: trim(get(shippingContact, 'givenName', '')),
  surname: trim(get(shippingContact, 'familyName', '')),
  email: trim(get(shippingContact, 'emailAddress', '')),
  mobile: normaliseMobile(get(shippingContact, 'phoneNumber')),
});

/**
 * Maps a Google Pay `PaymentData` response onto the guest checkout customer details shape. The
 * name and phone come from the billing address (requested via billingAddressParameters).
 */
const guestDetailsFromGooglePayData = paymentData => {
  const billingAddress = get(paymentData, 'paymentMethodData.info.billingAddress', {});

  return {
    ...splitFullName(get(billingAddress, 'name', '')),
    email: trim(get(paymentData, 'email', '')),
    mobile: normaliseMobile(get(billingAddress, 'phoneNumber')),
  };
};

/**
 * Which guest checkout fields (givenName, surname, email, mobile) fail the given yup schema.
 * Field names only — safe to log, unlike the values.
 */
const getInvalidGuestDetailFields = (schema, guestDetails) => {
  try {
    schema.validateSync(guestDetails, { abortEarly: false });
    return [];
  } catch (error) {
    return uniq(compact(get(error, 'inner', []).map(({ path }) => path)));
  }
};

const APPLE_PAY_NAME_ERROR = { contactField: 'name', message: 'Please enter your first name and surname' };
const APPLE_PAY_CONTACT_ERRORS = {
  givenName: APPLE_PAY_NAME_ERROR,
  surname: APPLE_PAY_NAME_ERROR,
  email: { contactField: 'emailAddress', message: 'Please enter a valid email address' },
  mobile: { contactField: 'phoneNumber', message: 'Please enter a valid mobile number' },
};

// Shown against the Apple Pay sheet's contact fields (see payWithApplePay's validateShippingContact).
const applePayContactErrors = invalidFields =>
  uniqBy(compact(invalidFields.map(field => APPLE_PAY_CONTACT_ERRORS[field])), 'contactField');

const GOOGLE_PAY_NAME_ERROR = 'Please add your first name and surname to your billing address';
const GOOGLE_PAY_ERROR_MESSAGES = {
  givenName: GOOGLE_PAY_NAME_ERROR,
  surname: GOOGLE_PAY_NAME_ERROR,
  mobile: 'Please add a valid mobile number to your billing address',
  email: 'Please use a Google account with a valid email address',
};

// Google Pay's sheet shows a single message, so surface the first problem.
const googlePayErrorMessage = invalidFields => {
  const field = invalidFields.find(invalidField => GOOGLE_PAY_ERROR_MESSAGES[invalidField]);
  return field ? GOOGLE_PAY_ERROR_MESSAGES[field] : null;
};

// Added to the /checkout URL when express checkout falls back because the wallet's details were
// incomplete, so the guest form can explain why the customer is there. A flag only — never details.
const EXPRESS_CHECKOUT_FALLBACK_PARAM = 'expressCheckout';
const EXPRESS_CHECKOUT_INCOMPLETE_DETAILS = 'incomplete-details';

export {
  EXPRESS_CHECKOUT_FALLBACK_PARAM,
  EXPRESS_CHECKOUT_INCOMPLETE_DETAILS,
  applePayContactErrors,
  getInvalidGuestDetailFields,
  googlePayErrorMessage,
  guestDetailsFromApplePayContact,
  guestDetailsFromGooglePayData,
  normaliseMobile,
};
